import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import type { ThemeFamily, ThemeVersion } from "@/lib/api/types";
import { pickLocalized } from "@/lib/i18n/localized";
import { getQueryErrorStatus } from "@/lib/query/core/get-query-error-status";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { NavigationGuardProvider } from "@/lib/navigation/navigationGuard";
import { BackButton } from "@/shared/layout/BackButton";
import { usePageTitle } from "@/shared/layout/page-title";
import { Badge, Banner, Button, ErrorState, KeyValues, LoadingState, StatusDot, useToast } from "@/shared/rd";
import { useThemeByIdQuery } from "../services/useThemeByIdQuery";
import { useCreateThemeVersionMutation } from "../services/useCreateThemeVersionMutation";
import { getLatestDraftVersion, sortVersionsDesc } from "../services/theme-versions";
import { ThemeEditForm } from "../components/ThemeEditForm";
import { ThemeStatusBadge } from "../components/ThemeStatusBadge";
import { ThemeStudio } from "../studio/ThemeStudio";

const FAMILY_LABEL_KEY: Record<ThemeFamily, string> = {
  starter: keys.themes.families.starter,
};

type ThemeDetailPageProps = {
  locale: Locale;
  themeId: string;
};

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale === "ar" ? "ar-EG" : "en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

const cardHead = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "16px 20px",
  borderBottom: "1px solid var(--rd-line-row)",
} as const;
const cardTitle = { margin: 0, fontSize: 15, fontWeight: 600, flex: 1 } as const;

export function ThemeDetailPage(props: ThemeDetailPageProps) {
  return (
    <NavigationGuardProvider key={props.themeId}>
      <ThemeDetailContent {...props} />
    </NavigationGuardProvider>
  );
}

function ThemeDetailContent({ locale, themeId }: ThemeDetailPageProps) {
  const t = useTranslations();
  const navigate = useNavigate();
  const toast = useToast();
  const { data, isLoading, error, refetch } = useThemeByIdQuery({ themeId });
  const { mutateAsync: createVersion, isPending: isCreatingVersion } = useCreateThemeVersionMutation();
  const themeErrorStatus = getQueryErrorStatus(error);
  /** A failed "New version": the server's words ("" when it had none). */
  const [actionError, setActionError] = useState<string | null>(null);

  usePageTitle(
    data ? pickLocalized(locale, data.nameAr, data.nameEn) : undefined,
    undefined,
    data?.slug,
  );

  useEffect(() => {
    if (themeErrorStatus === 404) {
      navigate(`/${locale}/admin/themes`, { replace: true });
    }
  }, [locale, navigate, themeErrorStatus]);

  const back = <BackButton label={t(keys.common.back)} fallbackHref={`/${locale}/admin/themes`} />;

  const page = t("themes.pageNameOne");
  if (isLoading) {
    return <LoadingState title={t(keys.shell.loadingTitle, { page })} sub={t(keys.shell.loadingSub)} rows={4} />;
  }

  if (themeErrorStatus === 404) {
    return null;
  }

  if (error || !data) {
    return (
      <>
        {back}
        <ErrorState
          title={t(keys.shell.errorTitle, { page })}
          sub={t(keys.shell.errorSub)}
          retryLabel={t(keys.shell.retry)}
          onRetry={() => void refetch()}
        />
      </>
    );
  }

  const versions = sortVersionsDesc(data.versions);
  const latestDraft = getLatestDraftVersion(data.versions);
  // The studio edits the open draft; with none, it shows the newest version read-only.
  const studioVersion: ThemeVersion | undefined = latestDraft ?? versions[0];
  const otherName = locale === "ar" ? data.nameEn : data.nameAr;
  const familyKey = FAMILY_LABEL_KEY[data.family];

  const handleCreateVersion = async () => {
    setActionError(null);
    try {
      const created = await createVersion({ themeId: data.id });
      toast.show({
        message:
          created && typeof created.version === "number"
            ? t("themes.versionCreatedNamed", { version: created.version })
            : t("themes.versionCreated"),
      });
    } catch (caught) {
      setActionError(caught instanceof ApiJsonClientError ? caught.message || "" : "");
    }
  };

  const versionStatus = (version: ThemeVersion) =>
    version.publishedAt !== null ? (
      <StatusDot tone="green">{t(keys.themes.published)}</StatusDot>
    ) : (
      <StatusDot tone="grey">{t(keys.themes.draft)}</StatusDot>
    );

  return (
    <>
      {back}

      {actionError !== null ? (
        <Banner
          tone="error"
          title={t("themes.versionCreateFailedTitle")}
          actions={
            <Button
              size="sm"
              loading={isCreatingVersion}
              loadingLabel={t(keys.shell.retrying)}
              onClick={() => void handleCreateVersion()}
            >
              {t(keys.shell.retry)}
            </Button>
          }
        >
          {[actionError, t("themes.nothingChanged")].filter(Boolean).join(" ")}
        </Banner>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 380px), 1fr))",
          gap: 16,
          alignItems: "start",
        }}
      >
        <section className="rd-card" style={{ overflow: "hidden" }}>
          <div style={cardHead}>
            <Badge id="star" size={36} />
            <h2 style={{ ...cardTitle, fontSize: 16 }}>{t(keys.themes.metadataTitle)}</h2>
            <Button
              variant="secondary"
              size="sm"
              icon={Plus}
              // Only one open draft at a time — a second would orphan this one
              // (the backend also enforces this). Edit/publish the draft first.
              disabled={Boolean(latestDraft)}
              loading={isCreatingVersion}
              loadingLabel={t("themes.creatingVersion")}
              title={latestDraft ? t("themes.newVersionBlockedHint") : t("themes.newVersionHint")}
              onClick={() => void handleCreateVersion()}
            >
              {t(keys.themes.newVersion)}
            </Button>
          </div>
          <div style={{ padding: "16px 20px" }}>
            <KeyValues
              rows={[
                {
                  label: t(keys.common.name),
                  value: (
                    <>
                      {pickLocalized(locale, data.nameAr, data.nameEn)}
                      {otherName ? (
                        <>
                          {" · "}
                          <bdi dir="auto">{otherName}</bdi>
                        </>
                      ) : null}
                    </>
                  ),
                },
                {
                  label: t(keys.themes.slug),
                  value: (
                    <span dir="ltr" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13 }}>
                      {data.slug}
                    </span>
                  ),
                },
                { label: t(keys.themes.family), value: familyKey ? t(familyKey) : data.family },
                { label: t(keys.common.status), value: <ThemeStatusBadge status={data.status} /> },
                {
                  label: t(keys.themes.niches),
                  value: data.niches.length
                    ? data.niches
                        .map((niche) => {
                          const key = (keys.themes.nicheLabels as Record<string, string>)[niche];
                          return key ? t(key) : niche;
                        })
                        .join(locale === "ar" ? "، " : ", ")
                    : "—",
                },
                {
                  label: t(keys.themes.sortWeight),
                  value: <span className="rd-num">{data.sortWeight.toLocaleString(locale === "ar" ? "ar-EG" : "en-US")}</span>,
                },
              ]}
            />
          </div>
        </section>

        <section className="rd-card" style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 4 }}>
          <h2 style={{ ...cardTitle, margin: "0 0 6px" }}>{t(keys.themes.versionsTitle)}</h2>
          {versions.length === 0 ? (
            <p style={{ margin: 0, color: "var(--rd-text-meta)" }}>{t(keys.themes.noDraftHint)}</p>
          ) : (
            versions.map((version) => (
              <div
                key={version.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "8px 0",
                  borderBottom: "1px solid var(--rd-line-row)",
                }}
              >
                {version.thumbnailUrl ? (
                  <img
                    src={version.thumbnailUrl}
                    alt={t(keys.themes.thumbnailPreviewAlt)}
                    style={{ width: 56, height: 36, flex: "none", borderRadius: 4, objectFit: "cover" }}
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    style={{
                      width: 56,
                      height: 36,
                      flex: "none",
                      borderRadius: 4,
                      background: "var(--rd-surface-sunken)",
                      boxShadow: "inset 0 0 0 1px var(--rd-line-header)",
                    }}
                  />
                )}
                <span style={{ flex: 1, fontWeight: 500 }}>
                  {t(keys.themes.versionLabel, { version: version.version })}
                </span>
                {version.publishedAt ? (
                  <span style={{ fontSize: 12, color: "var(--rd-text-meta)" }}>
                    {t(keys.themes.publishedAt)}: {formatDate(version.publishedAt, locale)}
                  </span>
                ) : null}
                {versionStatus(version)}
              </div>
            ))
          )}
        </section>
      </div>

      {studioVersion ? (
        <ThemeStudio
          key={`${data.id}:${studioVersion.id}`}
          themeId={data.id}
          version={studioVersion}
          heading={`${t(keys.themes.versionLabel, { version: studioVersion.version })} · ${
            studioVersion.publishedAt !== null ? t(keys.themes.published) : t(keys.themes.draft)
          }`}
          locale={locale === "ar" ? "ar" : "en"}
          detailsPanel={<ThemeEditForm theme={data} />}
          // No draft: the newest version is published and immutable, so the
          // studio shows its sections + preview read-only. "New version" in the
          // details card branches a draft to edit.
          readOnly={!latestDraft}
        />
      ) : (
        // Truly no versions at all — the metadata form is the only editor.
        <section className="rd-card" style={{ overflow: "hidden" }}>
          <div style={cardHead}>
            <h2 style={cardTitle}>{t("themes.editDetailsTitle")}</h2>
          </div>
          <div style={{ padding: "16px 20px" }}>
            <ThemeEditForm theme={data} />
          </div>
        </section>
      )}
    </>
  );
}
